import { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function fetchEnrolments(
  courseId,
  includeMaster = false
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&select=*,students(id,student_code,english_name,chinese_name,is_master)`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  const data = await response.json();

  return (Array.isArray(data) ? data : [])
    .filter(
      enrolment =>
        includeMaster ||
        enrolment?.students?.is_master !== true
    )
    .sort((a, b) =>
      String(a?.students?.student_code || '').localeCompare(
        String(b?.students?.student_code || ''),
        undefined,
        {
          numeric: true,
          sensitivity: 'base'
        }
      )
    );
}

async function fetchAllStudents(
  includeMaster = false
) {
  const masterFilter =
    includeMaster
      ? ''
      : '&is_master=eq.false';

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/students?active=eq.true${masterFilter}&select=id,student_code,english_name,chinese_name,is_master&order=student_code.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchStudentsByIds(
  studentCodes,
  includeMaster = false
) {
  const masterFilter =
    includeMaster
      ? ''
      : '&is_master=eq.false';

  const codeList = studentCodes
    .map(c => `"${c}"`)
    .join(',');

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/students?student_code=in.(${codeList})${masterFilter}&select=id,student_code,english_name,chinese_name,is_master`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}

async function enrolStudents(rows) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates,return=representation'
      },
      body: JSON.stringify(rows)
    }
  );
  return response.json();
}

async function updateEnrolment(id, data) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }
  );
}

async function fetchCoursePlacementLabels(
  courseId
) {
  const [
    streamResponse,
    groupResponse
  ] =
    await Promise.all([
      fetch(
        `${SUPABASE_URL}/rest/v1/course_stream_labels?course_id=eq.${courseId}&select=stream_number,name`,
        {
          headers: {
            apikey:
              SUPABASE_KEY,
            Authorization:
              'Bearer ' +
              SUPABASE_KEY
          }
        }
      ),

      fetch(
        `${SUPABASE_URL}/rest/v1/course_group_labels?course_id=eq.${courseId}&select=stream_number,group_number,name,description`,
        {
          headers: {
            apikey:
              SUPABASE_KEY,
            Authorization:
              'Bearer ' +
              SUPABASE_KEY
          }
        }
      )
    ]);

  if (
    !streamResponse.ok ||
    !groupResponse.ok
  ) {
    throw new Error(
      'Could not load Stream / Group titles.'
    );
  }

  const [
    streamRows,
    groupRows
  ] =
    await Promise.all([
      streamResponse.json(),
      groupResponse.json()
    ]);

  const streams = {};

  (
    Array.isArray(streamRows)
      ? streamRows
      : []
  ).forEach(row => {
    streams[
      String(
        row.stream_number
      )
    ] =
      String(
        row.name || ''
      ).trim();
  });


  const groups = {};

  (
    Array.isArray(groupRows)
      ? groupRows
      : []
  ).forEach(row => {
    groups[
      `${row.stream_number}|${row.group_number}`
    ] = {
      name:
        String(
          row.name || ''
        ).trim(),

      description:
        String(
          row.description ||
          ''
        ).trim()
    };
  });


  return {
    streams,
    groups
  };
}


async function saveStreamTitle(
  courseId,
  streamNumber,
  name,
  cmsToken
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/save_course_stream_label_instructor`,
      {
        method: 'POST',

        headers: {
          apikey:
            SUPABASE_KEY,

          Authorization:
            'Bearer ' +
            SUPABASE_KEY,

          'Content-Type':
            'application/json'
        },

        body:
          JSON.stringify({
            p_course_id:
              courseId,

            p_stream_number:
              streamNumber,

            p_name:
              name,

            p_cms_token:
              cmsToken
          })
      }
    );

  const result =
    await response.json();

  if (
    !response.ok ||
    !result?.ok
  ) {
    throw new Error(
      result?.message ||
      'Could not save the Stream title.'
    );
  }

  return result;
}


async function saveGroupDetails(
  courseId,
  streamNumber,
  groupNumber,
  name,
  description,
  cmsToken
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/save_course_group_label_instructor`,
      {
        method: 'POST',

        headers: {
          apikey:
            SUPABASE_KEY,

          Authorization:
            'Bearer ' +
            SUPABASE_KEY,

          'Content-Type':
            'application/json'
        },

        body:
          JSON.stringify({
            p_course_id:
              courseId,

            p_stream_number:
              streamNumber,

            p_group_number:
              groupNumber,

            p_name:
              name,

            p_description:
              description,

            p_cms_token:
              cmsToken
          })
      }
    );

  const result =
    await response.json();

  if (
    !response.ok ||
    !result?.ok
  ) {
    throw new Error(
      result?.message ||
      'Could not save the Group information.'
    );
  }

  return result;
}

async function enrolOrReactivateStudents(
  studentIds,
  courseId,
  existingEnrolments
) {
  const uniqueStudentIds = [
    ...new Set(
      studentIds
        .filter(Boolean)
        .map(id => String(id))
    )
  ];

  const existingByStudentId = new Map(
    existingEnrolments.map(enrolment => [
      String(enrolment.student_id),
      enrolment
    ])
  );

  const toReactivate = [];
  const toCreate = [];
  let alreadyActiveCount = 0;

  uniqueStudentIds.forEach(studentId => {
    const existing =
      existingByStudentId.get(studentId);

    if (existing) {
      if (existing.active) {
        alreadyActiveCount += 1;
      } else {
        toReactivate.push(existing.id);
      }

      return;
    }

    toCreate.push({
      student_id: studentId,
      course_id: courseId,
      active: true
    });
  });

  await Promise.all(
    toReactivate.map(enrolmentId =>
      updateEnrolment(
        enrolmentId,
        { active: true }
      )
    )
  );

  if (toCreate.length) {
    await enrolStudents(toCreate);
  }

  return {
    changedCount:
      toReactivate.length +
      toCreate.length,
    alreadyActiveCount
  };
}

async function fetchAllCourses(currentCourseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?select=id,name,course_title_id,semester_id,course_titles(name),semesters(name,start_date)&order=created_at.desc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const data = await response.json();
  const current = data.find(c => c.id === currentCourseId);
  const currentStart = current?.semesters?.start_date || null;
  return data.filter(c => {
    if (c.id === currentCourseId) return false;
    if (currentStart && c.semesters?.start_date) {
      return c.semesters.start_date < currentStart;
    }
    return true;
  });
}

async function fetchEnrolmentsForCourse(
  courseId,
  includeMaster = false
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=student_id,students(is_master)`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  const data = await response.json();

  return (Array.isArray(data) ? data : []).filter(
    enrolment =>
      includeMaster ||
      enrolment?.students?.is_master !== true
  );
}

function getStudentName(student) {
  return student?.english_name || student?.chinese_name || student?.student_code || '—';
}

// ─── Stream & Group Management Panel ────────────────────────────────────────

function StreamGroupPanel({
  courseId,
  cmsToken,
  enrolments,
  canEditMetadata,
  onUpdate
}) {
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [
  streamLabels,
  setStreamLabels
] = useState({});

const [
  groupLabels,
  setGroupLabels
] = useState({});

const [
  metadataSaving,
  setMetadataSaving
] = useState('');

const [
  metadataError,
  setMetadataError
] = useState('');


useEffect(() => {
  let cancelled = false;

  fetchCoursePlacementLabels(
    courseId
  )
    .then(result => {
      if (cancelled) {
        return;
      }

      setStreamLabels(
        result.streams
      );

      setGroupLabels(
        result.groups
      );
    })
    .catch(err => {
      console.error(
        'Loading Stream / Group titles failed:',
        err
      );

      if (!cancelled) {
        setMetadataError(
          err?.message ||
          'Could not load Stream / Group titles.'
        );
      }
    });

  return () => {
    cancelled = true;
  };
}, [courseId]);


function groupKey(
  stream,
  group
) {
  return `${stream}|${group}`;
}


function streamTitle(
  stream
) {
  return (
    streamLabels[
      String(stream)
    ] ||
    `Stream ${stream}`
  );
}


function groupInfo(
  stream,
  group
) {
  return (
    groupLabels[
      groupKey(
        stream,
        group
      )
    ] || {
      name: '',
      description: ''
    }
  );
}


function groupTitle(
  stream,
  group
) {
  return (
    groupInfo(
      stream,
      group
    ).name ||
    `Group ${group}`
  );
}


async function renameStream(
  stream
) {
  const current =
    streamLabels[
      String(stream)
    ] || '';

  const next =
    window.prompt(
      `Stream title. Leave blank to restore "Stream ${stream}".`,
      current
    );

  if (next === null) {
    return;
  }

  setMetadataSaving(
    `stream-${stream}`
  );

  setMetadataError('');

  try {
    const result =
      await saveStreamTitle(
        courseId,
        stream,
        next,
        cmsToken
      );

    setStreamLabels(
      currentLabels => ({
        ...currentLabels,
        [String(stream)]:
          result.name || ''
      })
    );

  } catch (err) {
    setMetadataError(
      err?.message ||
      'Could not rename the Stream.'
    );

  } finally {
    setMetadataSaving('');
  }
}


async function renameGroup(
  stream,
  group
) {
  const key =
    groupKey(
      stream,
      group
    );

  const current =
    groupInfo(
      stream,
      group
    );

  const next =
    window.prompt(
      `Group name. Leave blank to restore "Group ${group}".`,
      current.name
    );

  if (next === null) {
    return;
  }

  setMetadataSaving(
    `group-${key}`
  );

  setMetadataError('');

  try {
    const result =
      await saveGroupDetails(
        courseId,
        stream,
        group,
        next,
        current.description,
        cmsToken
      );

    setGroupLabels(
      currentLabels => ({
        ...currentLabels,

        [key]: {
          name:
            result.name ||
            '',

          description:
            result.description ||
            ''
        }
      })
    );

  } catch (err) {
    setMetadataError(
      err?.message ||
      'Could not rename the Group.'
    );

  } finally {
    setMetadataSaving('');
  }
}


async function editGroupDescription(
  stream,
  group
) {
  const key =
    groupKey(
      stream,
      group
    );

  const current =
    groupInfo(
      stream,
      group
    );

  const next =
    window.prompt(
      'Brief Group description. Leave blank for no description.',
      current.description
    );

  if (next === null) {
    return;
  }

  setMetadataSaving(
    `description-${key}`
  );

  setMetadataError('');

  try {
    const result =
      await saveGroupDetails(
        courseId,
        stream,
        group,
        current.name,
        next,
        cmsToken
      );

    setGroupLabels(
      currentLabels => ({
        ...currentLabels,

        [key]: {
          name:
            result.name ||
            '',

          description:
            result.description ||
            ''
        }
      })
    );

  } catch (err) {
    setMetadataError(
      err?.message ||
      'Could not save the Group description.'
    );

  } finally {
    setMetadataSaving('');
  }
}

  // Derive current streams from enrolments
  const streams = [...new Set(
    enrolments
      .map(e => e.stream)
      .filter(s => s !== null && s !== undefined)
  )].sort((a, b) => a - b);

  // Derive max group per stream
  function groupsInStream(stream) {
    return [...new Set(
      enrolments
        .filter(e => e.stream === stream && e.group_number !== null)
        .map(e => e.group_number)
    )].sort((a, b) => a - b);
  }

  // Students not yet assigned to any stream
  const unassigned = enrolments.filter(e => e.stream === null || e.stream === undefined);

  // Students in a given stream+group
  function membersOf(stream, group) {
    return enrolments.filter(e => e.stream === stream && e.group_number === group);
  }

  // Students in a stream but no group
  function ungroupedInStream(stream) {
    return enrolments.filter(e => e.stream === stream && (e.group_number === null || e.group_number === undefined));
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAll(ids) {
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
  }

  async function assignToStream(stream) {
    if (!selected.size) return;
    setSaving(true);
    try {
      await Promise.all(
        [...selected].map(id => updateEnrolment(id, { stream, group_number: null }))
      );
      onUpdate();
      setSelected(new Set());
    } finally {
      setSaving(false);
    }
  }

  async function assignToGroup(stream, group) {
    if (!selected.size) return;
    setSaving(true);
    try {
      await Promise.all(
        [...selected].map(id => updateEnrolment(id, { stream, group_number: group }))
      );
      onUpdate();
      setSelected(new Set());
    } finally {
      setSaving(false);
    }
  }

  async function removeFromStream(enrolmentId) {
    setSaving(true);
    try {
      await updateEnrolment(enrolmentId, { stream: null, group_number: null });
      onUpdate();
    } finally {
      setSaving(false);
    }
  }

  async function removeFromGroup(enrolmentId) {
    const e = enrolments.find(en => en.id === enrolmentId);
    if (!e) return;
    setSaving(true);
    try {
      await updateEnrolment(enrolmentId, { group_number: null });
      onUpdate();
    } finally {
      setSaving(false);
    }
  }

  async function addNewStream() {
    const nextStream = (streams.length > 0 ? Math.max(...streams) : 0) + 1;
    // Just add to state — no DB change until students are assigned
    // We trigger a re-render by marking stream as "pending"
    // Actually just show the stream column immediately by assigning one placeholder
    // The UI will show the new stream column after first assignment
    // For now, just assign selected students to the new stream
    await assignToStream(nextStream);
  }

  async function addGroupToStream(stream) {
    const groups = groupsInStream(stream);
    const nextGroup = groups.length > 0 ? Math.max(...groups) + 1 : 1;
    await assignToGroup(stream, nextGroup);
  }

  const selectedCount = selected.size;

  return (
    <div style={sgStyles.wrap}>
      <div style={sgStyles.instructions}>
        Check students to select them, then use the buttons to assign them to streams and groups.
        A new stream or group is created automatically when you assign students to it.
      </div>

      {metadataError && (
        <div style={{ color: '#b42318', fontSize: '12px', marginBottom: '10px' }}>
          {metadataError}
        </div>
      )}

      <div style={sgStyles.layout}>
        {/* Unassigned column */}
        <div style={sgStyles.column}>
          <div style={sgStyles.columnHeader}>
            <div style={sgStyles.columnTitle}>Unassigned</div>
            {unassigned.length > 0 && (
              <button
                style={sgStyles.selectAllBtn}
                onClick={() => selectAll(unassigned.map(e => e.id))}
              >
                Select all
              </button>
            )}
          </div>

          <div style={sgStyles.studentList}>
            {unassigned.length === 0 && (
              <div style={sgStyles.emptyCol}>All students assigned</div>
            )}
            {unassigned.map(e => (
              <label key={e.id} style={sgStyles.studentRow}>
                <input
                  type="checkbox"
                  checked={selected.has(e.id)}
                  onChange={() => toggleSelect(e.id)}
                />
                <span style={sgStyles.studentCode}>{e.students?.student_code}</span>
                <span style={sgStyles.studentName}>{getStudentName(e.students)}</span>
              </label>
            ))}
          </div>

          {selectedCount > 0 && (
            <div style={sgStyles.actionRow}>
              <span style={sgStyles.actionLabel}>{selectedCount} selected →</span>
              {streams.map(s => (
                <button
                  key={s}
                  style={sgStyles.assignBtn}
                  disabled={saving}
                  onClick={() => assignToStream(s)}
                >
                  {streamTitle(s)}
                </button>
              ))}
              <button
                style={{ ...sgStyles.assignBtn, ...sgStyles.newBtn }}
                disabled={saving}
                onClick={addNewStream}
              >
                + New Stream
              </button>
            </div>
          )}
        </div>

        {/* Stream columns */}
        {streams.map(stream => {
          const groups = groupsInStream(stream);
          const ungrouped = ungroupedInStream(stream);

          return (
            <div key={stream} style={sgStyles.streamColumn}>
              <div style={sgStyles.streamHeader}>
  <span>
    {streamTitle(stream)}
  </span>

  {canEditMetadata && (
    <button
      type="button"
      style={sgStyles.metaButton}
      disabled={
        Boolean(
          metadataSaving
        )
      }
      onClick={() =>
        renameStream(
          stream
        )
      }
    >
      Rename
    </button>
  )}
</div>

              {/* Ungrouped in this stream */}
              {ungrouped.length > 0 && (
                <div style={sgStyles.groupBlock}>
                  <div style={sgStyles.groupLabel}>No group</div>
                  {ungrouped.map(e => (
                    <div key={e.id} style={sgStyles.assignedRow}>
                      <label style={sgStyles.studentRow}>
                        <input
                          type="checkbox"
                          checked={selected.has(e.id)}
                          onChange={() => toggleSelect(e.id)}
                        />
                        <span style={sgStyles.studentCode}>{e.students?.student_code}</span>
                        <span style={sgStyles.studentName}>{getStudentName(e.students)}</span>
                      </label>
                      <button
                        style={sgStyles.removeBtn}
                        onClick={() => removeFromStream(e.id)}
                        title="Remove from stream"
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Groups */}
              {groups.map(group => (
                <div key={group} style={sgStyles.groupBlock}>
                  <div style={sgStyles.groupHeader}>
  <div style={sgStyles.groupLabel}>
    {groupTitle(
      stream,
      group
    )}
  </div>

  {canEditMetadata && (
    <div style={sgStyles.groupButtons}>
      <button
        type="button"
        style={sgStyles.metaButton}
        disabled={
          Boolean(
            metadataSaving
          )
        }
        onClick={() =>
          renameGroup(
            stream,
            group
          )
        }
      >
        Rename
      </button>

      <button
        type="button"
        style={sgStyles.metaButton}
        disabled={
          Boolean(
            metadataSaving
          )
        }
        onClick={() =>
          editGroupDescription(
            stream,
            group
          )
        }
      >
        Description
      </button>
    </div>
  )}
</div>

{groupInfo(
  stream,
  group
).description && (
  <div
    style={
      sgStyles.groupDescription
    }
  >
    {groupInfo(
      stream,
      group
    ).description}
  </div>
)}
                  {membersOf(stream, group).map(e => (
                    <div key={e.id} style={sgStyles.assignedRow}>
                      <label style={sgStyles.studentRow}>
                        <input
                          type="checkbox"
                          checked={selected.has(e.id)}
                          onChange={() => toggleSelect(e.id)}
                        />
                        <span style={sgStyles.studentCode}>{e.students?.student_code}</span>
                        <span style={sgStyles.studentName}>{getStudentName(e.students)}</span>
                      </label>
                      <button
                        style={sgStyles.removeBtn}
                        onClick={() => removeFromGroup(e.id)}
                        title="Remove from group"
                      >✕</button>
                    </div>
                  ))}
                </div>
              ))}

              {selectedCount > 0 && (
                <div style={sgStyles.actionRow}>
                  <span style={sgStyles.actionLabel}>{selectedCount} →</span>
                  {groups.map(g => (
                    <button
                      key={g}
                      style={sgStyles.assignBtn}
                      disabled={saving}
                      onClick={() => assignToGroup(stream, g)}
                    >
                      {groupTitle(stream, g)}
                    </button>
                  ))}
                  <button
                    style={{ ...sgStyles.assignBtn, ...sgStyles.newBtn }}
                    disabled={saving}
                    onClick={() => addGroupToStream(stream)}
                  >
                    + Group
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {/* Add new stream button when no streams yet */}
        {streams.length === 0 && selectedCount > 0 && (
          <div style={sgStyles.column}>
            <div style={sgStyles.columnHeader}>
              <div style={sgStyles.columnTitle}>Streams</div>
            </div>
            <div style={sgStyles.emptyCol}>No streams yet</div>
            <div style={sgStyles.actionRow}>
              <button
                style={{ ...sgStyles.assignBtn, ...sgStyles.newBtn }}
                disabled={saving}
                onClick={addNewStream}
              >
                + Create Stream 1
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const sgStyles = {
  wrap: { marginTop: '0' },
  instructions: { fontSize: '13px', color: '#66788a', marginBottom: '16px', lineHeight: '1.5' },
  layout: { display: 'flex', gap: '16px', overflowX: 'auto', alignItems: 'flex-start' },
  column: {
    minWidth: '220px', border: '1px solid #d7dee5', borderRadius: '10px',
    padding: '14px', background: 'white', flexShrink: 0
  },
  streamColumn: {
    minWidth: '220px', border: '1px solid #b0cfe0', borderRadius: '10px',
    padding: '14px', background: '#f7fbff', flexShrink: 0
  },
  columnHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  columnTitle: { fontWeight: '700', fontSize: '14px', color: '#1f2933' },
  streamHeader: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  fontWeight: '700',
  fontSize: '14px',
  color: '#215b7d',
  marginBottom: '10px',
  borderBottom: '1px solid #b0cfe0',
  paddingBottom: '8px'
},
  selectAllBtn: { fontSize: '11px', color: '#215b7d', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' },
  studentList: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px', maxHeight: '400px', overflowY: 'auto' },
  studentRow: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', padding: '3px 0' },
  assignedRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  studentCode: { fontWeight: '700', color: '#1f2933', minWidth: '60px' },
  studentName: { color: '#42515f' },
  groupBlock: { marginBottom: '12px' },
  groupLabel: { fontSize: '11px', fontWeight: '700', color: '#66788a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' },
  groupHeader: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  marginBottom: '4px'
},

groupButtons: {
  display: 'flex',
  gap: '4px'
},

groupDescription: {
  color: '#66788a',
  fontSize: '12px',
  lineHeight: '1.35',
  margin: '-1px 0 6px'
},

metaButton: {
  border: '1px solid #b8c5cf',
  borderRadius: '5px',
  background: '#fff',
  color: '#42515f',
  padding: '2px 5px',
  fontSize: '10px',
  fontWeight: '700',
  cursor: 'pointer'
},
  emptyCol: { fontSize: '13px', color: '#66788a', padding: '8px 0', fontStyle: 'italic' },
  actionRow: { display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid #edf1f4', paddingTop: '8px', marginTop: '4px' },
  actionLabel: { fontSize: '11px', color: '#66788a', fontWeight: '700' },
  assignBtn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '6px',
    padding: '4px 8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer'
  },
  newBtn: { background: '#216e45' },
  removeBtn: {
    background: 'none', border: 'none', color: '#a12a2a', cursor: 'pointer',
    fontSize: '12px', padding: '2px 4px', flexShrink: 0
  },
};

// ─── Main Component ──────────────────────────────────────────────────────────

export default function CourseStudents({
  course,
  session
}) {
  const [enrolments, setEnrolments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [addMode, setAddMode] = useState('line');
  const [allStudents, setAllStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [listSearch, setListSearch] = useState('');
  const [copySourceId, setCopySourceId] = useState('');
  const [allCourses, setAllCourses] = useState([]);
  const [activePanel, setActivePanel] = useState('students'); // 'students' | 'streams'
  const hasStreams = enrolments.some(e => e.stream !== null && e.stream !== undefined);

  async function loadAll() {
    return Promise.all([
  fetchEnrolments(
    course.id,
    session?.isSuperadmin === true
  ),
  fetchAllCourses(course.id),
  fetchAllStudents(
    session?.isSuperadmin === true
  )
]).then(([e, c, s]) => {
  setEnrolments(e);
  setAllCourses(c);
  setAllStudents(s);
      setSelectedStudentIds([]);
      setCopySourceId('');
      setListSearch('');
      setAddMode('line');
    }).finally(() => setLoading(false));
  }

  useEffect(() => { loadAll(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    course.id,
    session?.isSuperadmin
  ]);

  async function handleEnrol(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    const codes = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
    if (!codes.length) return setError('Enter at least one student ID.');
    setSaving(true);
    try {
      const found =
        await fetchStudentsByIds(
          codes,
          session?.isSuperadmin === true
        );
      const foundCodes = found.map(s => s.student_code);
      const notFound = codes.filter(c => !foundCodes.includes(c));
      if (notFound.length) {
        setError(`These student IDs were not found: ${notFound.join(', ')}`);
        setSaving(false);
        return;
      }
            const result =
        await enrolOrReactivateStudents(
          found.map(student => student.id),
          course.id,
          enrolments
        );
      const updated = await fetchEnrolments(
        course.id,
        session?.isSuperadmin === true
      );
      setEnrolments(updated);
      setBulkText('');
            if (result.changedCount === 0) {
        setSuccess(
          'All of those students are already enrolled.'
        );
      } else {
        setSuccess(
          `${result.changedCount} student${
            result.changedCount !== 1 ? 's' : ''
          } enrolled.${
            result.alreadyActiveCount
              ? ` ${result.alreadyActiveCount} already enrolled.`
              : ''
          }`
        );
      }
    } catch (err) {
      setError('Failed to enrol students.');
    } finally {
      setSaving(false);
    }
  }

  async function handleEnrolSelectedStudents() {
    setError('');
    setSuccess('');
    if (!selectedStudentIds.length) return setError('Select at least one student.');
    const activeStudentIds = new Set(enrolments.filter(e => e.active).map(e => e.student_id));
    const selected = allStudents.filter(s => selectedStudentIds.includes(s.id) && !activeStudentIds.has(s.id));
    if (!selected.length) return setError('The selected students are already enrolled.');
    setSaving(true);
    try {
            await enrolOrReactivateStudents(
        selected.map(student => student.id),
        course.id,
        enrolments
      );
      const updated = await fetchEnrolments(
        course.id,
        session?.isSuperadmin === true
      );
      setEnrolments(updated);
      setSelectedStudentIds([]);
      setSuccess(`${selected.length} student${selected.length !== 1 ? 's' : ''} enrolled.`);
    } catch (err) {
      setError('Failed to enrol selected students.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSetActive(enrolmentId, active) {
    await updateEnrolment(enrolmentId, { active });
    setEnrolments(prev => prev.map(e => e.id === enrolmentId ? { ...e, active } : e));
  }

  async function handleSetStream(enrolmentId, stream) {
    const value = stream ? Number(stream) : null;
    await updateEnrolment(enrolmentId, { stream: value });
    setEnrolments(prev => prev.map(e => e.id === enrolmentId ? { ...e, stream: value } : e));
  }

  async function handleSetGroup(enrolmentId, value) {
    const groupNumber = value ? Number(value) : null;
    await updateEnrolment(enrolmentId, { group_number: groupNumber });
    setEnrolments(prev => prev.map(e => e.id === enrolmentId ? { ...e, group_number: groupNumber } : e));
  }

    async function handleCopyFromCourse(sourceCourseId) {
    if (!sourceCourseId) return;
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const sourceEnrolments =
        await fetchEnrolmentsForCourse(
          sourceCourseId,
          session?.isSuperadmin === true
        );
            const existingIds = new Set(
        enrolments
          .filter(e => e.active)
          .map(e => e.student_id)
      );

      const toEnrol = sourceEnrolments
        .filter(
          e =>
            !existingIds.has(e.student_id)
        )
        .map(e => e.student_id);

      if (!toEnrol.length) {
        setSuccess(
          'All students from that course are already enrolled.'
        );
        setSaving(false);
        return;
      }

      await enrolOrReactivateStudents(
        toEnrol,
        course.id,
        enrolments
      );
      const updated = await fetchEnrolments(
        course.id,
        session?.isSuperadmin === true
      );
      setEnrolments(updated);
      setSuccess(
  `${toEnrol.length} student${
    toEnrol.length !== 1 ? 's' : ''
  } copied.`
);
    } catch (err) {
      setError('Failed to copy enrolments.');
    } finally {
      setSaving(false);
    }
  }

  const activeEnrolments = enrolments.filter(e => e.active);
  const activeStudentIds = new Set(activeEnrolments.map(e => e.student_id));

  const availableStudents = allStudents
    .filter(s => !activeStudentIds.has(s.id))
    .filter(s => {
      const term = listSearch.trim().toLowerCase();
      if (!term) return true;
      return [s.student_code, s.english_name, s.chinese_name]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(term));
    });

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <div style={styles.panelTabs}>
        <button
          style={{ ...styles.panelTab, ...(activePanel === 'students' ? styles.panelTabActive : {}) }}
          onClick={() => setActivePanel('students')}
        >
          Students
        </button>
        <button
          style={{ ...styles.panelTab, ...(activePanel === 'streams' ? styles.panelTabActive : {}) }}
          onClick={() => setActivePanel('streams')}
        >
          Streams & Groups
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {activePanel === 'streams' && (
        <StreamGroupPanel
  courseId={course.id}
  cmsToken={session.cmsToken}
  enrolments={activeEnrolments}
  canEditMetadata={
    session?.isSuperadmin === true ||
    course.instructor_id ===
      session?.instructorId
  }
  onUpdate={() =>
    fetchEnrolments(
      course.id,
      session?.isSuperadmin === true
    ).then(
      setEnrolments
    )
  }
/>
      )}

      {activePanel === 'students' && (
        <>
          <div style={styles.addForm}>
            <h3 style={styles.addHeading}>Enrol Students</h3>
            <div style={styles.modePills}>
              {['line', 'list', 'duplicate'].map(mode => (
                <button
                  key={mode}
                  type="button"
                  style={{ ...styles.modePill, ...(addMode === mode ? styles.modePillActive : {}) }}
                  onClick={() => setAddMode(mode)}
                >
                  {mode === 'line' ? 'Add by line' : mode === 'list' ? 'Add from list' : 'Duplicate course enrolment'}
                </button>
              ))}
            </div>

            {addMode === 'line' && (
              <form onSubmit={handleEnrol}>
                <div style={styles.field}>
                  <label style={styles.label}>Student IDs (one per line)</label>
                  <textarea
                    style={styles.textarea}
                    value={bulkText}
                    onChange={e => setBulkText(e.target.value)}
                    placeholder={'B11234\nB11235\nB11236'}
                    rows={5}
                  />
                </div>
                <button style={styles.btn} disabled={saving}>
                  {saving ? 'Enrolling...' : 'Enrol Students'}
                </button>
              </form>
            )}

            {addMode === 'list' && (
              <div>
                <div style={styles.listControls}>
                  <input
                    style={styles.listSearch}
                    type="text"
                    value={listSearch}
                    onChange={e => setListSearch(e.target.value)}
                    placeholder="Search by student ID or name..."
                  />
                  <div style={styles.selectionMeta}>{selectedStudentIds.length} selected</div>
                </div>
                {availableStudents.length === 0 ? (
                  <div style={styles.emptyList}>No students available to add.</div>
                ) : (
                  <div style={styles.checklist}>
                    {availableStudents.map(student => (
                      <label key={student.id} style={styles.checklistRow}>
                        <input
                          type="checkbox"
                          checked={selectedStudentIds.includes(student.id)}
                          onChange={() => setSelectedStudentIds(prev =>
                            prev.includes(student.id)
                              ? prev.filter(id => id !== student.id)
                              : [...prev, student.id]
                          )}
                        />
                        <span style={styles.checklistCode}>{student.student_code}</span>
                        <span style={styles.checklistName}>{student.english_name || '—'}</span>
                        <span style={styles.checklistChinese}>{student.chinese_name || '—'}</span>
                      </label>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  style={{ ...styles.btn, marginTop: '12px' }}
                  disabled={saving || selectedStudentIds.length === 0}
                  onClick={handleEnrolSelectedStudents}
                >
                  {saving ? 'Enrolling...' : `Enrol Selected${selectedStudentIds.length ? ` (${selectedStudentIds.length})` : ''}`}
                </button>
              </div>
            )}

            {addMode === 'duplicate' && (
              <div>
                {allCourses.length === 0 ? (
                  <div style={styles.emptyList}>No previous courses available.</div>
                ) : (
                  <div style={styles.copyPanel}>
                    <div style={styles.field}>
                      <label style={styles.label}>Previous course</label>
                      <select
                        style={styles.input}
                        value={copySourceId}
                        onChange={e => setCopySourceId(e.target.value)}
                      >
                        <option value="">Select a previous course...</option>
                        {allCourses.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.course_titles?.name || c.name || 'Untitled'} — {c.semesters?.name || 'Semester unknown'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      style={styles.btn}
                      disabled={saving || !copySourceId}
                      onClick={() => handleCopyFromCourse(copySourceId)}
                    >
                      {saving ? 'Copying...' : 'Duplicate Enrolment'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {activeEnrolments.length === 0 && (
            <div style={styles.muted}>No students enrolled yet.</div>
          )}

          {activeEnrolments.length > 0 && (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Student ID</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Chinese Name</th>
                    {hasStreams && <th style={styles.th}>Stream</th>}
                    {hasStreams && <th style={styles.th}>Group</th>}
                    <th style={styles.th}></th>
                                    </tr>
                </thead>
                <tbody>
                  {activeEnrolments.map(enrolment => {
                    const student = enrolment.students;
                    return (
                      <tr key={enrolment.id}>
                        <td style={styles.td}>{student?.student_code}</td>
                        <td style={styles.td}>{student?.english_name || '—'}</td>
                        <td style={styles.td}>{student?.chinese_name || '—'}</td>
                        {hasStreams && (
                          <td style={styles.td}>
                            <select
                              style={styles.smallSelect}
                              value={enrolment.stream ?? ''}
                              onChange={e => handleSetStream(enrolment.id, e.target.value)}
                            >
                              <option value="">—</option>
                              {[...new Set(enrolments.map(e => e.stream).filter(Boolean))].sort().map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                              <option value={Math.max(...enrolments.map(e => e.stream || 0)) + 1}>
                                New stream {Math.max(...enrolments.map(e => e.stream || 0)) + 1}
                              </option>
                            </select>
                          </td>
                        )}
                        {hasStreams && (
                          <td style={styles.td}>
                            <input
                              style={{ ...styles.smallSelect, width: '60px' }}
                              type="number"
                              min="1"
                              value={enrolment.group_number || ''}
                              onChange={e => handleSetGroup(enrolment.id, e.target.value)}
                            />
                          </td>
                        )}
                        <td style={styles.td}>
                          <button style={styles.dangerBtn} onClick={() => handleSetActive(enrolment.id, false)}>
                            Remove
                          </button>
                        </td>
                                             </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

                  </>
      )}
    </div>
  );
}

const styles = {
  panelTabs: { display: 'flex', gap: '4px', borderBottom: '2px solid #d7dee5', marginBottom: '20px' },
  panelTab: {
    border: 'none', background: 'transparent', padding: '10px 16px',
    fontSize: '14px', fontWeight: '600', color: '#66788a', cursor: 'pointer',
    borderBottom: '2px solid transparent', marginBottom: '-2px'
  },
  panelTabActive: { color: '#215b7d', borderBottom: '2px solid #215b7d' },
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 20px', lineHeight: '1.5' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  modePills: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '18px' },
  modePill: {
    border: '1px solid #d7dee5', background: 'white', color: '#66788a',
    borderRadius: '999px', padding: '7px 13px', fontSize: '12px', fontWeight: '700', cursor: 'pointer'
  },
  modePillActive: { background: '#215b7d', borderColor: '#215b7d', color: 'white' },
  listControls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' },
  listSearch: { flex: 1, minWidth: '220px', border: '1px solid #d7dee5', borderRadius: '8px', padding: '8px 10px', fontSize: '13px', background: 'white' },
  selectionMeta: { color: '#66788a', fontSize: '12px', fontWeight: '700' },
  checklist: { border: '1px solid #d7dee5', borderRadius: '8px', background: 'white', maxHeight: '310px', overflowY: 'auto' },
  checklistRow: { display: 'grid', gridTemplateColumns: '24px 110px minmax(150px, 1fr) minmax(100px, 1fr)', alignItems: 'center', gap: '8px', padding: '8px 10px', borderBottom: '1px solid #edf1f4', fontSize: '13px', cursor: 'pointer' },
  checklistCode: { fontWeight: '700', color: '#1f2933' },
  checklistName: { color: '#42515f' },
  checklistChinese: { color: '#66788a' },
  emptyList: { color: '#66788a', fontSize: '13px', padding: '12px 0' },
  copyPanel: { maxWidth: '620px' },
  addForm: { border: '1px solid #d7dee5', borderRadius: '10px', padding: '20px', background: '#f7f9fa', marginBottom: '24px' },
  addHeading: { margin: '0 0 16px', fontSize: '16px', color: '#1f2933' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#1f2933' },
  textarea: { width: '100%', border: '1px solid #d7dee5', borderRadius: '8px', padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box', background: 'white', fontFamily: 'monospace', resize: 'vertical' },
  input: { flex: 1, border: '1px solid #d7dee5', borderRadius: '8px', padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box', background: 'white' },
  btn: { background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  secondaryBtn: { background: 'transparent', color: '#66788a', border: '1px solid #d7dee5', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' },
  restoreBtn: { background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0', borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer' },
  tableWrap: { border: '1px solid #d7dee5', borderRadius: '10px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '9px 11px', fontSize: '13px', color: '#42515f', background: '#f0f4f7', borderBottom: '1px solid #d7dee5' },
  td: { padding: '9px 11px', borderBottom: '1px solid #d7dee5', fontSize: '14px' },
  smallSelect: { border: '1px solid #d7dee5', borderRadius: '6px', padding: '5px 8px', fontSize: '13px', background: 'white' },
  dangerBtn: { background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8', borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer' },
};